import React, {
  createContext,
  useState,
  useRef,
  useContext,
  useCallback,
} from 'react'

const MusicContext = createContext()

export const useMusic = () => useContext(MusicContext)

/** Stabil nyckel för dubblettkontroll i användarkön (musik eller ljudbok). */
function playlistItemKey(item) {
  if (!item) return ''
  if (item.type === 'music') return `music|${item.artist}|${item.title}`
  if (item.type === 'audio-book')
    return `audio-book|${item.book}|${item.chapter}`
  return ''
}

/** Hitta index för nuvarande spår (currentTrack-format) i en spellista med API-objekt. */
function findTrackInPlaylist(playlist, track) {
  if (!track || !playlist?.length) return -1
  if (track.type === 'music') {
    return playlist.findIndex(
      (s) =>
        s.type === 'music' &&
        s.title === track.title &&
        s.artist === track.creator,
    )
  }
  if (track.type === 'audio-book') {
    return playlist.findIndex(
      (s) =>
        s.type === 'audio-book' &&
        s.chapter === track.title &&
        s.book === track.creator,
    )
  }
  return -1
}

function toCurrentTrack(item) {
  //this function unifies audio-books and music so they both fit into the currentTrack variable
  if (!item) return null
  if (item.type === 'audio-book') {
    return {
      type: 'audio-book',
      creator: item.book,
      title: item.chapter,
    }
  }
  if (item.type === 'music') {
    return {
      type: 'music',
      creator: item.artist,
      title: item.title,
    }
  }
  return null
}

export const MusicProvider = ({ children }) => {
  //   console.log('MusicProvider is being rendered')
  const [artists, setArtists] = useState([])
  const [selectedArtist, setSelectedArtist] = useState(null)
  const [selectedBook, setSelectedBook] = useState(null)
  const [artistSongs, setArtistSongs] = useState([])
  const [bookChapters, setBookChapters] = useState([])
  const [isArtistLoading, setIsArtistLoading] = useState(false)
  const [isBookLoading, setIsBookLoading] = useState(false)
  const [currentSongIndex, setCurrentSongIndex] = useState(-1)
  const [currentTrack, setCurrentTrack] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = useRef(null)
  const [fallbackPlaylist, setFallbackPlaylist] = useState([])
  const [userPlaylist, setUserPlaylist] = useState([])
  const [isLooping, setIsLooping] = useState(false)
  const [isPlayerOpen, setIsPlayerOpen] = useState(false)

  const userPlaylistRef = useRef(userPlaylist)
  const currentSongIndexRef = useRef(currentSongIndex)
  const currentTrackRef = useRef(currentTrack)
  const fallbackPlaylistRef = useRef(fallbackPlaylist)
  const artistRequestIdRef = useRef(0)
  const bookRequestIdRef = useRef(0)
  userPlaylistRef.current = userPlaylist
  currentSongIndexRef.current = currentSongIndex
  currentTrackRef.current = currentTrack
  fallbackPlaylistRef.current = fallbackPlaylist

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || window.location.origin
  const API_URL = `${API_BASE_URL}/api`

  const initializePlayerFromQueue = useCallback((item) => {
    const nextTrack = toCurrentTrack(item)
    if (!nextTrack) return

    setCurrentTrack(nextTrack)
    setCurrentSongIndex(0)
    setIsPlaying(false)
  }, [])

  const addToUserPlaylist = useCallback(
    (item) => {
      if (!item || (item.type !== 'music' && item.type !== 'audio-book')) return

      setUserPlaylist((prev) => {
        const key = playlistItemKey(item)
        if (!key || prev.some((s) => playlistItemKey(s) === key)) return prev
        return [...prev, item]
      })
      /** If the player is not open then we should open it using the item that was sent to addToUserPlaylist */
      if (!isPlayerOpen) {
        setIsPlayerOpen(true)
        initializePlayerFromQueue(item)
      }
    },
    [initializePlayerFromQueue, isPlayerOpen],
  )

  const addBookChaptersToUserPlaylist = useCallback(
    (chapters) => {
      if (!Array.isArray(chapters) || chapters.length === 0) return

      let firstQueuedItem = null

      setUserPlaylist((prev) => {
        const existing = new Set(prev.map(playlistItemKey))
        const toAdd = chapters.filter(
          (c) =>
            c &&
            c.type === 'audio-book' &&
            playlistItemKey(c) &&
            !existing.has(playlistItemKey(c)),
        )
        if (toAdd.length === 0) return prev
        firstQueuedItem = toAdd[0]
        toAdd.forEach((c) => existing.add(playlistItemKey(c)))
        return [...prev, ...toAdd]
      })

      if (!isPlayerOpen && firstQueuedItem) {
        setIsPlayerOpen(true)
        initializePlayerFromQueue(firstQueuedItem)
      }
    },
    [initializePlayerFromQueue, isPlayerOpen],
  )

  const fetchChapters = useCallback(
    async (book) => {
      if (!book) return

      try {
        const response = await fetch(`${API_URL}/book/${book}/chapters`)
        // console.log('Response status:', response.status)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const data = await response.json()
        console.log('bookChapters:', data)
        return data
      } catch (error) {
        console.error('Failed to load chapters:', error)
      }
    },
    [API_URL],
  )

  const addBookToUserPlaylist = useCallback(
    async (bookName) => {
      if (!bookName) return

      try {
        const chapters = await fetchChapters(bookName)
        if (!Array.isArray(chapters) || chapters.length === 0) return
        addBookChaptersToUserPlaylist(chapters)
      } catch (error) {
        console.error('Failed to add book chapters to playlist:', error)
      }
    },
    [addBookChaptersToUserPlaylist, fetchChapters],
  )

  const clearUserPlaylist = useCallback(() => {
    setUserPlaylist([])
  }, [])

  const syncCurrentSongIndexToFallback = useCallback(() => {
    if (!currentTrack) {
      setCurrentSongIndex(-1)
      return
    }
    if (currentTrack.type === 'music') {
      const i = fallbackPlaylist.findIndex(
        (t) =>
          t.type === 'music' &&
          t.title === currentTrack.title &&
          t.artist === currentTrack.creator,
      )
      setCurrentSongIndex(i)
      return
    }
    if (currentTrack.type === 'audio-book') {
      const i = fallbackPlaylist.findIndex(
        (t) =>
          t.type === 'audio-book' &&
          t.chapter === currentTrack.title &&
          t.book === currentTrack.creator,
      )
      setCurrentSongIndex(i)
    }
  }, [currentTrack, fallbackPlaylist])

  const updateCurrentTrack = (track) => {
    if (track == null) {
      setCurrentTrack(null)
      return
    }
    const nextTrack = toCurrentTrack(track)
    if (nextTrack) {
      setCurrentTrack(nextTrack)
    }
  }

  const moveUserPlaylistItem = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex) return
    setUserPlaylist((prev) => {
      const next = [...prev]
      const [removed] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, removed)
      return next
    })
    setCurrentSongIndex((playingIndex) => {
      if (playingIndex < 0) return playingIndex
      if (playingIndex === fromIndex) return toIndex
      if (fromIndex < toIndex) {
        if (playingIndex > fromIndex && playingIndex <= toIndex) {
          return playingIndex - 1
        }
      } else if (fromIndex > toIndex) {
        if (playingIndex >= toIndex && playingIndex < fromIndex) {
          return playingIndex + 1
        }
      }
      return playingIndex
    })
  }, [])

  const removeUserPlaylistIndices = useCallback((rawIndices) => {
    const removeSet = new Set(
      [...rawIndices].filter((i) => Number.isInteger(i) && i >= 0),
    )
    if (removeSet.size === 0) return

    const prev = userPlaylistRef.current
    const next = prev.filter((_, i) => !removeSet.has(i))
    const track = currentTrackRef.current
    const oldPlaying = currentSongIndexRef.current

    if (next.length === 0) {
      setUserPlaylist([])
      const fb = fallbackPlaylistRef.current
      const idx = findTrackInPlaylist(fb, track)
      const newTrack = idx >= 0 ? fb[idx] : null
      setCurrentSongIndex(idx)
      updateCurrentTrack(newTrack)
      return
    }

    setUserPlaylist(next)

    const stillInQueue = findTrackInPlaylist(next, track)
    if (stillInQueue >= 0) {
      const item = next[stillInQueue]
      setCurrentSongIndex(stillInQueue)
      updateCurrentTrack(item)
      return
    }

    if (track?.type !== 'music' && track?.type !== 'audio-book') {
      return
    }

    if (oldPlaying < 0) {
      return
    }

    const removedBefore = [...removeSet].filter((i) => i < oldPlaying).length
    const newIndex = Math.max(
      0,
      Math.min(oldPlaying - removedBefore, next.length - 1),
    )
    const item = next[newIndex]
    setCurrentSongIndex(newIndex)
    updateCurrentTrack(item)
  }, [])

  const playSong = (selectedTrack, index) => {
    setIsPlayerOpen(true)
    if (selectedTrack.type === 'audio-book') {
      //Check of user clicked on the same chapter that is already playing.
      const sameChapter =
        currentTrack?.type === 'audio-book' &&
        selectedTrack.chapter === currentTrack.title &&
        selectedTrack.book === currentTrack.creator
      if (sameChapter) {
        if (isPlaying) {
          audioRef.current.pause()
        } else {
          audioRef.current.play()
        }
        setIsPlaying(!isPlaying)
      } else {
        updateCurrentTrack(selectedTrack)
        setCurrentSongIndex(index)
        setIsPlaying(true)
      }
    } else if (selectedTrack.type === 'music') {
      const sameSong =
        currentTrack?.type === 'music' &&
        selectedTrack.title === currentTrack.title &&
        selectedTrack.artist === currentTrack.creator
      if (sameSong) {
        if (isPlaying) {
          audioRef.current.pause()
        } else {
          audioRef.current.play()
        }
        setIsPlaying(!isPlaying)
      } else {
        updateCurrentTrack(selectedTrack)
        setCurrentSongIndex(index)
        setIsPlaying(true)
      }
    }
  }

  const fetchSongs = useCallback(
    async (artist) => {
      if (!artist) return

      try {
        const response = await fetch(`${API_URL}/artist/${artist}/songs`)
        // console.log('Response status:', response.status)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const data = await response.json()
        console.log('artistSongs:', data)
        return data
      } catch (error) {
        console.error('Failed to load songs:', error)
      }
    },
    [API_URL],
  )

  const stopMusic = useCallback(() => {
    console.log(
      'stopMusic function is running - stopping audio and resetting state',
    )
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      console.log('Audio paused and reset to beginning')
    } else {
      console.log('No audio reference found')
    }
    setIsPlaying(false)
    setCurrentTrack(null)
    setCurrentSongIndex(-1)
    console.log('Music state reset complete')
  }, [])

  const resetPlayerAndQueues = useCallback(() => {
    stopMusic()
    setFallbackPlaylist([])
    setUserPlaylist([])
    setIsLooping(false)
    setIsPlayerOpen(false)
  }, [stopMusic])

  const handleArtistSelect = useCallback(
    async (artistName) => {
      if (!artistName) return

      const requestId = ++artistRequestIdRef.current
      setSelectedArtist(artistName)
      setIsArtistLoading(true)
      setArtistSongs([])

      try {
        const response = await fetchSongs(artistName)
        if (artistRequestIdRef.current !== requestId) return
        setArtistSongs(Array.isArray(response) ? response : [])
      } catch (error) {
        console.error('Failed to fetch songs:', error)
        if (artistRequestIdRef.current === requestId) {
          setArtistSongs([])
        }
      } finally {
        if (artistRequestIdRef.current === requestId) {
          setIsArtistLoading(false)
        }
      }
    },
    [fetchSongs],
  )
  const handleBookSelect = useCallback(
    async (bookName) => {
      if (!bookName) return

      const requestId = ++bookRequestIdRef.current
      setSelectedBook(bookName)
      setIsBookLoading(true)
      setBookChapters([])

      try {
        const response = await fetchChapters(bookName)
        if (bookRequestIdRef.current !== requestId) return
        setBookChapters(Array.isArray(response) ? response : [])
      } catch (error) {
        console.error('Failed to fetch chapters:', error)
        if (bookRequestIdRef.current === requestId) {
          setBookChapters([])
        }
      } finally {
        if (bookRequestIdRef.current === requestId) {
          setIsBookLoading(false)
        }
      }
    },
    [fetchChapters],
  )

  return (
    <MusicContext.Provider
      value={{
        handleArtistSelect,
        handleBookSelect,
        selectedBook,
        setSelectedBook,
        bookChapters,
        setBookChapters,
        artists,
        setArtists,
        selectedArtist,
        setSelectedArtist,
        artistSongs,
        setArtistSongs,
        isArtistLoading,
        updateCurrentTrack,
        currentTrack,
        currentSongIndex,
        setCurrentSongIndex,
        playSong,
        stopMusic,
        resetPlayerAndQueues,
        fallbackPlaylist,
        setFallbackPlaylist,
        userPlaylist,
        setUserPlaylist,
        addToUserPlaylist,
        addBookToUserPlaylist,
        addBookChaptersToUserPlaylist,
        clearUserPlaylist,
        syncCurrentSongIndexToFallback,
        moveUserPlaylistItem,
        removeUserPlaylistIndices,
        audioRef,
        isPlaying,
        setIsPlaying,
        API_URL,
        isLooping,
        setIsLooping,
        isBookLoading,
        isPlayerOpen,
      }}
    >
      {children}
    </MusicContext.Provider>
  )
}
